<template>
  <main class="main p44">

    <Breadcrumbs :breadcrumbs="breadcrumbs" />

    <!-- Блок 1: Hero -->
    <section class="p44-hero">
      <div class="container p44-hero__container">
        <div class="p44-hero__content">
          <h1 class="p44-hero__title title">
            Гардеробные на заказ<br>по индивидуальному проекту
          </h1>
          <p class="p44-hero__desc">
            Не просто место хранения, а инструмент управления имиджем
          </p>
          <button class="btn main-btn" @click="isModalOpen = true">Рассчитать проект</button>
          <p class="p44-hero__geo">Работаем с объектами Москвы и Московской области</p>
        </div>
        <div class="p44-hero__image" v-if="heroImage">
          <img
            :src="heroImage"
            alt="Гардеробные на заказ по индивидуальному проекту"
            loading="eager"
          />
        </div>
      </div>
    </section>

    <!-- Блок 2: Виды гардеробных -->
    <section class="p44-types">
      <div class="container">
        <h2 class="title center p44-types__heading">Впишем в ваш интерьер</h2>
        <p class="p44-types__lead">Создаём гардеробные в любом стиле.</p>
      </div>
      <div class="p44-types__slider">
        <ClientOnly>
          <SliderSwiperNuxt :swiperSlides="typeSlides" :swiperSetting="typesSliderSettings">
            <template #sliderBody="{ slide }">
              <div class="p44-types__card">
                <div class="p44-types__card-img">
                  <img :src="slide.img" :alt="slide.name" loading="lazy" />
                </div>
                <div class="p44-types__card-name">{{ slide.name }}</div>
              </div>
            </template>
          </SliderSwiperNuxt>
        </ClientOnly>
      </div>
    </section>

    <!-- Блок 3: Почему мы -->
    <section class="p44-why">
      <div class="container">
        <h2 class="title center p44-why__heading">Почему мы</h2>
        <p class="p44-why__lead">Вы получите решение, которое сделает управление имиджем удобным.</p>
        <div class="p44-why__grid">
          <div v-for="item in whyItems" :key="item.title" class="p44-why__card">
            <div class="p44-why__icon" v-html="item.icon"></div>
            <div class="p44-why__card-title">{{ item.title }}</div>
            <div class="p44-why__card-text">{{ item.text }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- Блок 4: Таймлайн -->
    <section class="p44-timeline">
      <div class="container">
        <h2 class="title center p44-timeline__heading">Как будет происходить взаимодействие</h2>
        <p class="p44-timeline__lead">Сделали процесс максимально простым и понятным.</p>
        <div class="p44-timeline__track">
          <div class="p44-timeline__line" aria-hidden="true"></div>
          <div
            v-for="(step, i) in timelineSteps"
            :key="step.title"
            class="p44-timeline__row"
            :class="i % 2 === 0 ? 'p44-timeline__row--left' : 'p44-timeline__row--right'"
          >
            <div class="p44-timeline__dot" aria-hidden="true">
              <span class="p44-timeline__num">{{ i + 1 }}</span>
            </div>
            <div class="p44-timeline__card">
              <div class="p44-timeline__card-title">{{ step.title }}</div>
              <div class="p44-timeline__card-text">{{ step.text }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Блок 5: Fashion-галерея -->
    <section class="p44-gallery">
      <div class="container">
        <h2 class="title center p44-gallery__heading">Ваш стиль — способ общения</h2>
        <p class="p44-gallery__lead">
          От того, как вы выглядите, зависит успех коммуникации.<br>
          Ваш образ во многом зависит от гардероба и гардеробной.
        </p>
      </div>
      <div class="container p44-gallery__body">
        <ClientOnly>
          <MasonryWall :items="galleryItems" :column-width="360" :gap="16" :max-columns="3">
            <template #default="{ item, index }">
              <div class="p44-gallery__item" @click="openLightbox(index)">
                <img :src="item.src" :alt="'Fashion — кадр ' + (index + 1)" loading="lazy" />
              </div>
            </template>
          </MasonryWall>
        </ClientOnly>
      </div>
    </section>

    <!-- Блок 6: Форма заявки -->
    <section class="p44-form" id="request-form">
      <div class="container">
        <div class="p44-form__layout">

          <div class="p44-form__aside">
            <div class="p44-form__aside-label">Запрос</div>
            <h2 class="title p44-form__heading">Рассчитать проект</h2>
            <p class="p44-form__aside-text">
              Опишите задачу — специалист свяжется с вами для первичной консультации и замера.
            </p>
            <ul class="p44-form__aside-facts">
              <li>Бесплатный выезд на замер</li>
              <li>3D-визуализация проекта</li>
              <li>Производство и монтаж под ключ</li>
            </ul>
          </div>

          <div class="p44-form__body">
            <form @submit.prevent="submitRequest" novalidate>
              <div class="p44-form__row">
                <div class="p44-form__field">
                  <Input placeholder="Как вас зовут" v-model="reqName" class="p44-fi" />
                </div>
                <div class="p44-form__field">
                  <Input placeholder="+7 000 000-00-00" v-model="reqPhone" type="tel" class="p44-fi" />
                </div>
              </div>
              <div class="p44-form__field">
                <Input placeholder="Ваш email (необязательно)" v-model="reqEmail" type="email" class="p44-fi" />
              </div>
              <div class="p44-form__field">
                <Textarea
                  placeholder="Опишите задачу — размеры помещения, стиль интерьера, пожелания"
                  v-model="reqComment"
                  class="p44-ft"
                />
              </div>
              <div
                class="p44-form__drop"
                :class="{ 'is-over': isDragOver }"
                @dragover.prevent="isDragOver = true"
                @dragleave.prevent="isDragOver = false"
                @drop.prevent="onFormDrop"
                @click="formFileRef?.click()"
              >
                <input
                  ref="formFileRef"
                  type="file"
                  multiple
                  accept=".jpg,.jpeg,.png,.pdf,.dwg"
                  class="p44-form__file-input"
                  @change="onFormFilePick"
                />
                <template v-if="!reqFiles.length">
                  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                    <polyline points="17 8 12 3 7 8"/>
                    <line x1="12" y1="3" x2="12" y2="15"/>
                  </svg>
                  <span class="p44-form__drop-main">Прикрепите фото помещения, план или проект</span>
                  <span class="p44-form__drop-sub">Перетащите файлы сюда или нажмите для выбора · JPG, PNG, PDF, DWG · до 20 МБ</span>
                </template>
                <div v-else class="p44-form__drop-files">
                  <span v-for="f in reqFiles" :key="f.name" class="p44-form__drop-file">{{ f.name }}</span>
                </div>
              </div>
              <label class="p44-form__check">
                <input type="checkbox" class="p44-form__check-input" @change="reqAgreed = $event.target.checked" />
                <span class="p44-form__check-box"></span>
                <span class="p44-form__check-text">
                  Я согласен с <a href="/privacy" target="_blank">обработкой персональных данных</a> и <a href="/oferta" target="_blank">публичной офертой</a>
                </span>
              </label>
              <button type="submit" class="p44-form__btn" :disabled="!reqAgreed">Отправить запрос</button>
              <p v-if="reqSuccess" class="p44-form__success">Запрос отправлен — специалист свяжется с вами в ближайшее время.</p>
              <p v-if="reqError" class="p44-form__error">{{ reqError }}</p>
            </form>
          </div>

        </div>
      </div>
    </section>

    <!-- Блок 7: FAQ -->
    <section class="p44-faq">
      <div class="container">
        <h2 class="title p44-faq__heading">Часто задаваемые вопросы</h2>
        <div class="p44-faq__list">
          <Spoiler
            v-for="(item, i) in faqItems"
            :key="item.q"
            :item="{ label: item.q, content: item.a }"
            :isActive="faqActive === i"
            @toggleSpoiler="faqActive = faqActive === i ? null : i"
          />
        </div>
      </div>
    </section>

  </main>

  <teleport to="body">
    <FormWardrobes v-if="isModalOpen" @close="isModalOpen = false" />
    <Lightbox v-if="lbOpen" :items="galleryItems" :start-index="lbIndex" @close="lbOpen = false" />
    <Cookie />
  </teleport>
</template>

<script setup>
import Breadcrumbs from '~/components/Section/Breadcrumbs.vue'
import FormWardrobes from '~/components/Modals/FormWardrobes.vue'
import SliderSwiperNuxt from '~/components/UI/Slider/SliderSwiperNuxt.vue'
import Spoiler from '~/components/UI/Containers/Spoiler.vue'
import Input from '~/components/UI/inputs/Input.vue'
import Textarea from '~/components/UI/inputs/Textarea.vue'
import Lightbox from '~/components/Modals/Lightbox.vue'
import Cookie from '~/components/Section/Cookie.vue'
import { Product } from '~/api/Product.ts'
import api from '~/api/api.js'

const isModalOpen = ref(false)

const faqActive = ref(null)
const faqItems = [
  {
    q: 'С чего начать?',
    a: 'Отправьте заявку — опишите задачу и прикрепите фото помещения или размеры если есть. Если ничего нет — начнём с замера. Если есть дизайн-проект — сразу считаем по нему.'
  },
  {
    q: 'Сколько это стоит?',
    a: 'Стоимость индивидуальна — зависит от размеров помещения, материалов, фурнитуры и сложности дизайна. Отправьте заявку — подготовим расчёт оперативно.'
  },
  {
    q: 'Выезжает ли специалист на замер?',
    a: 'Да. Специалист партнёра приедет в удобное время, замерит помещение и все пути к нему — проёмы, лестничную площадку, входную группу. Проконсультирует по эргономике и поможет с выбором материалов. Стоимость замера в пределах МКАД — 1800 ₽. При заключении договора эта сумма вычитается из общей стоимости.'
  },
  {
    q: 'Есть ли шоурум?',
    a: 'Да, в Москве. Можно приехать, посмотреть материалы и фурнитуру вживую. Адрес сообщим после заявки — запись по предварительной договорённости.'
  },
  {
    q: 'Каковы сроки изготовления?',
    a: 'В среднем от 20 до 45 рабочих дней — зависит от объёма и материалов. Точный срок будет указан в коммерческом предложении.'
  },
]

const reqName    = ref('')
const reqPhone   = ref('')
const reqEmail   = ref('')
const reqComment = ref('')
const reqFiles   = ref([])
const reqAgreed  = ref(false)
const reqSuccess = ref(false)
const reqError   = ref('')
const isDragOver  = ref(false)
const formFileRef = ref(null)

watch(reqPhone, (val) => {
  let digits = val.replace(/\D/g, '')
  if (digits.startsWith('7') || digits.startsWith('8')) digits = digits.slice(1)
  digits = digits.slice(0, 10)
  let masked = '+7'
  if (digits.length > 0) masked += ' ' + digits.slice(0, 3)
  if (digits.length > 3) masked += ' ' + digits.slice(3, 6)
  if (digits.length > 6) masked += '-' + digits.slice(6, 8)
  if (digits.length > 8) masked += '-' + digits.slice(8, 10)
  if (reqPhone.value !== masked) reqPhone.value = masked
})

function onFormFilePick(e) {
  reqFiles.value = Array.from(e.target.files)
}

function onFormDrop(e) {
  isDragOver.value = false
  reqFiles.value = Array.from(e.dataTransfer.files)
}

async function submitRequest() {
  reqError.value = ''
  if (!reqName.value.trim() || !reqPhone.value.trim()) {
    reqError.value = 'Укажите имя и телефон.'
    return
  }
  if (!reqAgreed.value) {
    reqError.value = 'Подтвердите согласие.'
    return
  }
  const fd = new FormData()
  fd.append('product_id', '44')
  fd.append('name', reqName.value)
  fd.append('phone', reqPhone.value)
  if (reqEmail.value)   fd.append('email', reqEmail.value)
  if (reqComment.value) fd.append('comment', reqComment.value)
  reqFiles.value.forEach(f => fd.append('files[]', f))
  try {
    await api('/order', fd, 'POST')
    reqSuccess.value = true
    reqName.value = ''; reqPhone.value = ''; reqEmail.value = ''
    reqComment.value = ''; reqFiles.value = []
  } catch {
    reqError.value = 'Произошла ошибка. Попробуйте ещё раз или позвоните нам.'
  }
}

const lbOpen  = ref(false)
const lbIndex = ref(0)

function openLightbox(i) {
  lbIndex.value = i
  lbOpen.value  = true
}

const { data: galleryPaths } = await useFetch('/api/gallery/w')
const galleryItems = computed(() =>
  (galleryPaths.value ?? []).map(src => ({ src }))
)

let heroImage = null
try {
  const product = await Product.getInfo(44)
  heroImage = product.imgPath ?? null
} catch {
  heroImage = null
}

useHead({
  title: 'Гардеробные на заказ по индивидуальному проекту | Шарунстор',
  meta: [
    {
      name: 'description',
      content: 'Гардеробные на заказ по индивидуальному проекту. Не просто место хранения, а инструмент управления имиджем. Работаем с объектами Москвы и Московской области.'
    }
  ]
})

const typeSlides = [
  { name: 'Корпусная',         img: '/img/services/furniture/w-corp.jpg' },
  { name: 'Хай-тек',           img: '/img/services/furniture/w-hitek.jpg' },
  { name: 'Стеллажная',        img: '/img/services/furniture/w-stellazh.jpg' },
  { name: 'Стеклянные фасады', img: '/img/services/furniture/w-glass.jpg' },
]

const typesSliderSettings = {
  loop: false,
  breakpoints: {
    0:    { slidesPerView: 1.4, spaceBetween: 16 },
    768:  { slidesPerView: 2.5, spaceBetween: 20 },
    1200: { slidesPerView: 3.5, spaceBetween: 24 },
  },
}

const timelineSteps = [
  {
    title: 'Заявка от вас',
    text: 'Познакомимся и выясним ваши задачи.',
  },
  {
    title: 'Замер',
    text: 'Специалист партнёра выезжает к вам, чтобы замерить не только помещение, но и пути к нему — проёмы, лестничную площадку, входную группу.',
  },
  {
    title: 'Дизайн-проект',
    text: 'Увидите решение до того, как по нему начнутся работы.',
  },
  {
    title: 'Договор',
    text: 'Вы будете уверены в том, что задача будет решена.',
  },
  {
    title: 'Производство',
    text: 'Работа конструктора и изготовление по согласованному проекту.',
  },
  {
    title: 'Поставка и монтаж',
    text: 'Привезём, соберём, уберём за собой.',
  },
]

const whyItems = [
  {
    title: 'Учитываем ваш образ жизни',
    text: 'У вас будет удобный инструмент, а не просто система хранения.',
    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><circle cx="12" cy="7" r="4"/><path d="M4 21c0-4 3.6-7 8-7s8 3 8 7"/></svg>`,
  },
  {
    title: 'Проектируем под вас и пространство',
    text: 'Создадим функциональное и эстетически приятное решение.',
    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>`,
  },
  {
    title: 'Используем качественные и безопасные материалы',
    text: 'Вы будете уверены в своей гардеробной.',
    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/></svg>`,
  },
]

const breadcrumbs = [
  { title: 'Главная', url: '/' },
  { title: 'Каталог', url: '/catalog' },
  { title: 'Гардеробные на заказ' }
]
</script>

<style scoped lang="scss">
$mobile: 48em;
$tablet: 75em;
$dark: #1B1918;
$grey: #666;

.p44-hero {
  padding: 6rem 0 8rem;

  &__container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6rem;
    align-items: center;

    @media (max-width: $tablet) {
      grid-template-columns: 1fr;
      gap: 4rem;
    }
  }

  &__title {
    font-size: 5rem;
    line-height: 1.12;
    margin-bottom: 2.4rem;
    color: $dark;

    @media (max-width: $tablet) {
      font-size: 4rem;
    }

    @media (max-width: $mobile) {
      font-size: 3.2rem;
    }
  }

  &__desc {
    font-size: 1.8rem;
    line-height: 1.6;
    color: $grey;
    margin-bottom: 3.2rem;
    max-width: 52rem;
  }

  &__geo {
    margin-top: 1.6rem;
    font-size: 1.4rem;
    color: #aaa;
  }

  &__image {
    img {
      width: 100%;
      height: 56rem;
      object-fit: cover;
      border-radius: 0.4rem;
      display: block;

      @media (max-width: $tablet) {
        height: 40rem;
      }

      @media (max-width: $mobile) {
        height: 28rem;
      }
    }
  }
}
// ─── Блок 5: Форма заявки ────────────────────────────────────────────────────
.p44-form {
  padding: 10rem 0;
  background: $dark;

  &__layout {
    display: grid;
    grid-template-columns: 1fr 1.4fr;
    gap: 8rem;
    align-items: start;

    @media (max-width: $tablet) {
      grid-template-columns: 1fr;
      gap: 5rem;
    }
  }

  &__aside {
    padding-top: 1.6rem;
    color: #fff;
  }

  &__aside-label {
    font-size: 1.2rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.4);
    margin-bottom: 2.4rem;
  }

  &__heading {
    color: #fff;
    margin-bottom: 2.4rem;
    font-size: 4.8rem;

    @media (max-width: $tablet) { font-size: 4rem; }
  }

  &__aside-text {
    font-size: 1.6rem;
    line-height: 1.7;
    color: rgba(255,255,255,0.55);
    margin-bottom: 3.6rem;
  }

  &__aside-facts {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 1.2rem;

    li {
      font-size: 1.5rem;
      color: rgba(255,255,255,0.7);
      padding-left: 2rem;
      position: relative;

      &::before {
        content: '—';
        position: absolute;
        left: 0;
        color: rgba(255,255,255,0.3);
      }
    }
  }

  &__body {
    background: #fff;
    border-radius: 12px;
    padding: 4.8rem;

    @media (max-width: $mobile) { padding: 3.2rem 2.4rem; }

    form {
      display: flex;
      flex-direction: column;
      gap: 1.6rem;
    }
  }

  &__row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.6rem;

    @media (max-width: $mobile) { grid-template-columns: 1fr; }
  }

  &__field {
    display: flex;
    flex-direction: column;
  }

  &__drop {
    border: 1.5px dashed #D8D8D8;
    border-radius: 6px;
    padding: 2.8rem 2rem;
    text-align: center;
    cursor: pointer;
    transition: border-color 0.2s, background 0.2s;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.8rem;
    color: #B0B0B0;

    svg { color: #CCC; margin-bottom: 0.4rem; }
    &.is-over { border-color: $dark; background: #f5f5f5; }
    &:hover { border-color: #aaa; }
  }

  &__file-input { display: none; }

  &__drop-main {
    font-size: 1.5rem;
    color: $dark;
    font-weight: 500;
  }

  &__drop-sub {
    font-size: 1.3rem;
    color: #B0B0B0;
  }

  &__drop-files {
    display: flex;
    flex-wrap: wrap;
    gap: 0.8rem;
    justify-content: center;
  }

  &__drop-file {
    font-size: 1.3rem;
    background: #F0F0F0;
    border-radius: 4px;
    padding: 0.4rem 1rem;
    color: $dark;
  }

  &__check {
    display: flex;
    align-items: flex-start;
    gap: 1.2rem;
    cursor: pointer;
  }

  &__check-input {
    display: none;

    &:checked + .p44-form__check-box {
      background: $dark;
      border-color: $dark;

      &::after { opacity: 1; }
    }
  }

  &__check-box {
    flex-shrink: 0;
    width: 1.8rem;
    height: 1.8rem;
    border: 1.5px solid #C0C0C0;
    border-radius: 3px;
    margin-top: 0.2rem;
    position: relative;
    transition: background 0.15s, border-color 0.15s;

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 10'%3E%3Cpolyline points='1,5 4.5,8.5 11,1' fill='none' stroke='white' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") center / 1.1rem no-repeat;
      opacity: 0;
      transition: opacity 0.15s;
    }
  }

  &__check-text {
    font-size: 1.4rem;
    color: $grey;
    line-height: 1.5;

    a { color: $dark; text-decoration: underline; }
  }

  &__btn {
    width: 100%;
    height: 5.6rem;
    background: $dark;
    color: #fff;
    border: none;
    border-radius: 6px;
    font-size: 1.5rem;
    font-family: 'Manrope', sans-serif;
    font-weight: 600;
    letter-spacing: 0.04em;
    cursor: pointer;
    transition: background 0.2s;

    &:hover { background: #333; }
    &:disabled { opacity: 0.35; cursor: not-allowed; }
  }

  &__success {
    font-size: 1.5rem;
    color: #006307;
    text-align: center;
  }

  &__error {
    font-size: 1.4rem;
    color: #eb2a0d;
    text-align: center;
  }
}

.p44-fi {
  width: 100% !important;
  height: 5.2rem !important;
  padding: 0 2rem !important;
  font-size: 1.5rem !important;
  font-family: 'Manrope', sans-serif !important;
  color: $dark !important;
  background: #FAFAFA !important;
  border: 1.5px solid #D8D8D8 !important;
  border-radius: 6px !important;
  transition: border-color 0.2s, background 0.2s !important;

  &:focus {
    border-color: $dark !important;
    background: #fff !important;
    outline: none !important;
  }

  &::placeholder { color: #B0B0B0 !important; }
}

.p44-ft {
  width: 100% !important;
  height: 12rem !important;
  padding: 1.6rem 2rem !important;
  font-size: 1.5rem !important;
  font-family: 'Manrope', sans-serif !important;
  color: $dark !important;
  background: #FAFAFA !important;
  border: 1.5px solid #D8D8D8 !important;
  border-radius: 6px !important;
  resize: none !important;
  line-height: 1.6 !important;
  transition: border-color 0.2s, background 0.2s !important;

  &:focus {
    border-color: $dark !important;
    background: #fff !important;
    outline: none !important;
  }

  &::placeholder { color: #B0B0B0 !important; }
}

// ─── Блок 6: Fashion-галерея ─────────────────────────────────────────────────
.p44-gallery {
  padding: 8rem 0;

  &__heading {
    margin-bottom: 1.6rem;
  }

  &__lead {
    text-align: center;
    font-size: 1.8rem;
    color: $grey;
    max-width: 64rem;
    margin: 0 auto 5.6rem;
    line-height: 1.7;
  }

  &__body {
    max-width: 70%;
    margin: 0 auto;

    @media (max-width: $tablet) {
      max-width: 100%;
    }
  }

  &__item {
    cursor: pointer;
    border-radius: 6px;
    overflow: hidden;
    transition: opacity 0.2s;

    &:hover { opacity: 0.88; }

    img {
      width: 100%;
      display: block;
    }
  }
}

// ─── Блок 7: FAQ ─────────────────────────────────────────────────────────────
.p44-faq {
  padding: 8rem 0 10rem;

  &__heading {
    margin-bottom: 5.6rem;
    text-align: center;
  }

  &__list {
    max-width: 80rem;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  @media (max-width: $mobile) {
    padding: 5rem 0 6rem;

    &__heading { margin-bottom: 3.2rem; }
  }
}

// ─── Блок 4: Таймлайн ────────────────────────────────────────────────────────
.p44-timeline {
  padding: 8rem 0;
  background: #f7f7f7;

  &__heading {
    margin-bottom: 1.6rem;
  }

  &__lead {
    text-align: center;
    font-size: 1.8rem;
    color: $grey;
    margin: 0 auto 6rem;
    line-height: 1.6;
  }

  &__track {
    position: relative;
    max-width: 96rem;
    margin: 0 auto;
  }

  &__line {
    position: absolute;
    left: 50%;
    top: 0;
    bottom: 0;
    width: 1px;
    background: #D8D8D8;
    transform: translateX(-50%);
    z-index: 0;

    @media (max-width: $tablet) {
      left: 1.6rem;
      transform: none;
    }
  }

  // Каждая строка — сетка из 3 колонок: [контент] [точка] [контент]
  &__row {
    display: grid;
    grid-template-columns: 1fr 4.8rem 1fr;
    align-items: center;
    column-gap: 2.4rem;
    padding: 3rem 0;

    // Точка всегда в центре
    .p44-timeline__dot { grid-column: 2; }

    // Нечётные (0, 2, 4): карточка слева, выравнивание вправо
    &--left .p44-timeline__card {
      grid-column: 1;
      grid-row: 1;
      text-align: right;
    }

    // Чётные (1, 3, 5): карточка справа
    &--right .p44-timeline__card {
      grid-column: 3;
      grid-row: 1;
    }

    @media (max-width: $tablet) {
      grid-template-columns: 3.2rem 1fr;
      column-gap: 2rem;
      padding: 2rem 0;

      .p44-timeline__dot { grid-column: 1; grid-row: 1; }

      &--left .p44-timeline__card,
      &--right .p44-timeline__card {
        grid-column: 2;
        grid-row: 1;
        text-align: left;
      }
    }
  }

  &__dot {
    width: 4.8rem;
    height: 4.8rem;
    background: $dark;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    z-index: 1;
    justify-self: center;

    @media (max-width: $tablet) {
      width: 3.2rem;
      height: 3.2rem;
    }
  }

  &__num {
    font-size: 1.5rem;
    font-weight: 700;
    color: #fff;
    line-height: 1;

    @media (max-width: $tablet) {
      font-size: 1.3rem;
    }
  }

  &__card {
    max-width: 36rem;
  }

  &__card-title {
    font-size: 1.8rem;
    font-weight: 700;
    color: $dark;
    margin-bottom: 0.8rem;
    line-height: 1.3;
  }

  &__card-text {
    font-size: 1.5rem;
    color: $grey;
    line-height: 1.6;
  }
}

// ─── Блок 3: Почему мы ───────────────────────────────────────────────────────
.p44-why {
  padding: 8rem 0;

  &__heading {
    margin-bottom: 1.6rem;
  }

  &__lead {
    text-align: center;
    font-size: 1.8rem;
    color: $grey;
    max-width: 56rem;
    margin: 0 auto 5.6rem;
    line-height: 1.6;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 2.4rem;

    @media (max-width: $tablet) {
      grid-template-columns: 1fr;
      gap: 2rem;
    }
  }

  &__card {
    padding: 3.2rem;
    background: #f7f7f7;
    border-radius: 8px;
  }

  &__icon {
    margin-bottom: 2rem;
    color: $dark;

    svg { display: block; }
  }

  &__card-title {
    font-size: 1.8rem;
    font-weight: 700;
    color: $dark;
    line-height: 1.3;
    margin-bottom: 1.2rem;
  }

  &__card-text {
    font-size: 1.5rem;
    color: $grey;
    line-height: 1.6;
  }
}

// ─── Блок 2: Виды гардеробных ────────────────────────────────────────────────
.p44-types {
  padding: 8rem 0;
  background: #f7f7f7;

  &__heading {
    margin-bottom: 1.6rem;
  }

  &__lead {
    text-align: center;
    font-size: 1.8rem;
    color: $grey;
    margin: 0 auto 4rem;
    line-height: 1.6;
  }

  &__slider {
    padding-left: 3.2rem;

    @media (max-width: $mobile) {
      padding-left: 2rem;
    }
  }

  &__card {
    padding-right: 0.4rem;
  }

  &__card-img {
    img {
      width: 100%;
      aspect-ratio: 3 / 4;
      object-fit: cover;
      border-radius: 5px;
      display: block;
    }
  }

  &__card-name {
    padding: 1.4rem 0 0.4rem;
    font-size: 1.6rem;
    font-weight: 700;
    color: $dark;
    text-align: center;
  }
}
</style>
